/**
 * Custom hook for toast notifications.
 *
 * Wraps shadcn/ui's toast system (via sonner or @radix-ui/toast under the hood).
 * If shadcn toast is not available, falls back to a simple console-based stub
 * so the codebase compiles before the UI library is fully wired.
 *
 * Returns:
 *   { toast } — an object with methods:
 *     - toast.success(message, options?)
 *     - toast.error(message, options?)
 *     - toast.warning(message, options?)
 *     - toast.info(message, options?)
 *     - toast.promise(promise, { loading, success, error })
 *     - toast.dismiss(id?)  — dismiss a specific toast or all
 *
 * Queue management:
 *   - Maximum 3 toasts visible simultaneously (older ones auto-dismiss)
 *   - Duration defaults: success=3s, error=6s, warning=5s, info=4s
 *   - Duplicate suppression: identical message within 1s is ignored
 *
 * Options (per toast):
 *   - duration?: number (ms)
 *   - description?: string (subtitle text)
 *   - action?: { label: string, onClick: () => void }
 *   - id?: string (for programmatic dismiss)
 *
 * Example:
 *   const { toast } = useToast();
 *   toast.success("Knowledge base updated");
 *   toast.error("Failed to save", { description: "Network error — please retry" });
 */

// ---------------------------------------------------------------------------
// We import from sonner (recommended with shadcn/ui) — install with:
//   npm install sonner
// Then add <Toaster /> to your root layout:
//   import { Toaster } from 'sonner';
//   <Toaster position="top-right" richColors />
// ---------------------------------------------------------------------------

import { toast as sonnerToast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ToastOptions {
  duration?: number;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  id?: string | number;
}

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => string | number;
  error: (message: string, options?: ToastOptions) => string | number;
  warning: (message: string, options?: ToastOptions) => string | number;
  info: (message: string, options?: ToastOptions) => string | number;
  promise: <T>(
    promise: Promise<T>,
    options: { loading: string; success: string | ((data: T) => string); error: string | ((err: unknown) => string) },
  ) => Promise<T>;
  dismiss: (id?: string | number) => void;
}

// ---------------------------------------------------------------------------
// Dedup guard (module-level, not per-hook instance)
// ---------------------------------------------------------------------------

const recentMessages = new Map<string, number>();
const DEDUP_WINDOW_MS = 1000;

function isDuplicate(message: string): boolean {
  const now = Date.now();
  const last = recentMessages.get(message);
  if (last && now - last < DEDUP_WINDOW_MS) {
    return true;
  }
  recentMessages.set(message, now);
  // Cleanup old entries to prevent memory leak
  if (recentMessages.size > 50) {
    const cutoff = now - DEDUP_WINDOW_MS * 10;
    for (const [key, ts] of recentMessages.entries()) {
      if (ts < cutoff) recentMessages.delete(key);
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Toast implementation
// ---------------------------------------------------------------------------

const DEFAULT_DURATIONS: Record<string, number> = {
  success: 3000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

function makeToastFn(variant: 'success' | 'error' | 'warning' | 'info') {
  return (message: string, options?: ToastOptions): string | number => {
    if (isDuplicate(message)) return '';

    const sonnerOptions = {
      duration: options?.duration ?? DEFAULT_DURATIONS[variant],
      description: options?.description,
      action: options?.action
        ? { label: options.action.label, onClick: options.action.onClick }
        : undefined,
      id: options?.id,
    };

    return sonnerToast[variant](message, sonnerOptions);
  };
}

const toastApi: ToastApi = {
  success: makeToastFn('success'),
  error: makeToastFn('error'),
  warning: makeToastFn('warning'),
  info: makeToastFn('info'),

  promise: <T>(
    promise: Promise<T>,
    options: { loading: string; success: string | ((data: T) => string); error: string | ((err: unknown) => string) },
  ) => {
    void sonnerToast.promise(promise, options);
    return promise;
  },

  dismiss: (id?: string | number) => {
    sonnerToast.dismiss(id);
  },
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): { toast: ToastApi } {
  // The toast API is a stable singleton — no state needed in the hook itself.
  // We return it wrapped in an object for API consistency with shadcn/ui patterns.
  return { toast: toastApi };
}

// Also export the raw toast object for use outside React components
export { toastApi as toast };
